import { useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from '@xstate/react'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { fetchTourV2Offer } from 'actions/tour/TourV2Actions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { dateIsBeforeOrSame, intlDateFormatter } from 'lib/datetime/dateUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { formatRoomType } from 'lib/tours/tourUtils'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import SharedCartComments from '../Comments/SharedCartComments'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'

interface Props {
  multiCartItem: App.MultiCartTourV2Item
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function TourV2MultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  saveForLaterIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  let offerKey = multiCartItem.offerId
  if (multiCartItem.privateRequestKey) {
    offerKey = `${multiCartItem.offerId}-${multiCartItem.privateRequestKey}`
  }
  const {
    tourOffer,
    tourOfferLoading,
    tourOfferError,
  } = useAppSelector((state) => ({
    tourOffer: state.offer.tourV2Offers[offerKey],
    tourOfferLoading: state.offer.offersLoading[offerKey],
    tourOfferError: state.offer.offerErrors[offerKey],
  }), shallowEqual)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type === 'shared'

  const isDeleted = multiCartItem.state === 'deleted'
  const saveForLater = multiCartItem.state === 'save_for_later'
  const isPast = dateIsBeforeOrSame(new Date(multiCartItem.startDate), new Date())
  const isBookable = !isPast && !tourOfferError

  useEffect(() => {
    if (tourOffer || tourOfferLoading || tourOfferError) return
    dispatch(fetchTourV2Offer(multiCartItem.offerId, multiCartItem.privateRequestKey))
  }, [dispatch, multiCartItem.offerId, multiCartItem.privateRequestKey, tourOffer, tourOfferLoading, tourOfferError])

  useEffect(() => {
    if (!activeCartId) return
    if (isBookable) return
    if (saveForLater) return
    if (tourOfferLoading) return
    if (isDeleted) return
    if (!saveForLaterIfUnavailable) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      saveForLater: !saveForLater,
      skipNotification: true,
    })
  }, [activeCartId, isBookable, isDeleted, multiCartItem.id, multiCartReviewActorRef, saveForLater, saveForLaterIfUnavailable, tourOfferLoading])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      saveForLater: !saveForLater,
    })
  }, [activeCartId, multiCartItem.id, multiCartReviewActorRef, saveForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }, [activeCartId, multiCartActorRef, multiCartItem.id, multiCartItem.state])

  const formattedDateRange = useMemo(() => {
    const startDate = intlDateFormatter(multiCartItem.startDate, INTL_DMY_CASUAL_FORMAT, intl.locale)
    const endDate = intlDateFormatter(multiCartItem.endDate, INTL_DMY_CASUAL_FORMAT, intl.locale)
    return `${startDate} - ${endDate}`
  }, [intl.locale, multiCartItem.endDate, multiCartItem.startDate])

  const packageTitle = useMemo(() => {
    const parts: Array<string> = []
    const roomType = formatRoomType(multiCartItem.purchasableOption.roomType)

    if (multiCartItem.purchasableOption.packageOptionDisplayName) {
      parts.push(multiCartItem.purchasableOption.packageOptionDisplayName)
    }

    if (multiCartItem.purchasableOption.packageType) {
      parts.push(multiCartItem.purchasableOption.packageType)
    }

    if (roomType) {
      parts.push(roomType)
    }

    return parts.join(' · ')
  }, [multiCartItem.purchasableOption])

  const privateRequestQueryParams = useMemo(() => {
    if (!multiCartItem.privateRequestKey) {
      return undefined
    }

    const queryParams = new URLSearchParams()
    queryParams.set('privateRequestKey', multiCartItem.privateRequestKey)
    return queryParams
  }, [multiCartItem.privateRequestKey])

  const tourURL = useMemo(() => {
    if (tourOffer) {
      return offerPageURL(tourOffer, privateRequestQueryParams)
    }

    let url = `/tour/${multiCartItem.offerId}/${multiCartItem.purchasableOption.fkVariationId}`
    if (privateRequestQueryParams) {
      url = `${url}?${privateRequestQueryParams.toString()}`
    }
    return url
  }, [multiCartItem.offerId, multiCartItem.purchasableOption.fkVariationId, privateRequestQueryParams, tourOffer])

  const bannerTypeVariant = getBannerTypeVariant(isPast, false, false, !!tourOfferError)
  const title = tourOffer?.name || multiCartItem.purchasableOption.packageOptionDisplayName || ''
  const image = tourOffer?.image || multiCartItem.image
  const priceBreakdown = useMemo(() => {
    return generateDepartureRoomPriceBreakdown(
      multiCartItem.occupancy,
      multiCartItem.purchasableOption,
      tourOffer,
    )
  }, [multiCartItem.occupancy, multiCartItem.purchasableOption, tourOffer])
  const price = multiCartItem.total ?? priceBreakdown.totalPrice
  const memberPrice = priceBreakdown.totalMemberPrice
  const value = priceBreakdown.totalValuedAtPrice ?? 0

  if (tourOfferLoading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (isDeleted) {
    return <UndoDeletedItemsTile
      title={title}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        to={tourURL}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        onDelete={handleDelete}
        content={<CondensedCartReviewTileContent
          title={title}
          dates={formattedDateRange}
          packageTitle={packageTitle}
        />}
        imageElement={<CartReviewTileImageContent image={image} />}
        price={<CartReviewPriceStack
          price={price}
          memberPrice={memberPrice}
          value={value}
          luxPlusOfferConfig={tourOffer?.luxPlus}
        />}
      />
    )
  }

  let commentsFooter: React.ReactNode
  if (activeCartId && isSharedCart) {
    commentsFooter = (
      <SharedCartComments
        multiCartId={activeCartId}
        itemIds={[multiCartItem.id]}
        noBorder
      />
    )
  }

  return (
    <CartReviewTile
      to={tourURL}
      imageElement={<CartReviewTileImageContent image={image} />}
      content={<CartReviewTileContent
        title={title}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={packageTitle}
        label={<MultiCartParticipantLabel item={multiCartItem} />}
      />}
      price={<CartReviewPriceStack
        price={price}
        memberPrice={memberPrice}
        value={value}
        luxPlusOfferConfig={tourOffer?.luxPlus}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        to={tourURL}
      />}
      footer={commentsFooter}
      isBookable={isBookable}
      saveForLater={saveForLater}
      actions={<CartReviewTileActions
        isBookable={isBookable}
        saveForLater={saveForLater}
        onSaveForLater={handleSaveForLater}
        onDelete={handleDelete}
      />}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      disableOverlay={disableOverlay}
    />
  )
}

export default TourV2MultiCartReviewTile
